import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

// 导入 P5.js 组件
import P5ShinkaiBackground from '../components/home/P5ShinkaiBackground';

// 导入资源
import floatingSeeds from '../assets/ghibli/floating-seeds.svg';
import leafSvg from '../assets/ghibli/leaf.svg';

// 导入社交媒体图标
import twitterIcon from '../assets/icons/twitter.svg';
import githubIcon from '../assets/icons/github.svg';
import discordIcon from '../assets/icons/discord.svg';
import docsIcon from '../assets/icons/docs.svg';
import logoSvg from '/logo/MascotLogo.jpg';

// 文档分类和内容
const documentCategories = [
  {
    id: 'getting-started',
    title: '入门指南',
    icon: '🚀',
    documents: [
      { id: 'introduction', title: '项目简介', content: '平面世界是一个基于区块链的多人在线游戏，玩家可以在这个世界中探索、交互和创造。' },
      { id: 'installation', title: '安装指南', content: '如何安装和设置平面世界游戏客户端。' },
      { id: 'quick-start', title: '快速开始', content: '新手玩家的第一步指南，帮助你快速上手游戏。' }
    ]
  },
  {
    id: 'gameplay',
    title: '游戏玩法',
    icon: '🎮',
    documents: [
      { id: 'basic-controls', title: '基本操作', content: '学习如何在平面世界中移动、交互和使用基本功能。' },
      { id: 'character-creation', title: '角色创建', content: '如何创建和自定义你的游戏角色。' },
      { id: 'exploration', title: '世界探索', content: '探索平面世界的不同区域和发现隐藏的秘密。' }
    ]
  },
  {
    id: 'advanced',
    title: '进阶内容',
    icon: '⚙️',
    documents: [
      { id: 'blockchain', title: '区块链机制', content: '了解平面世界如何利用区块链技术实现去中心化游戏体验。' },
      { id: 'smart-contracts', title: '智能合约', content: '平面世界中智能合约的工作原理和应用。' },
      { id: 'economy', title: '游戏经济', content: '平面世界的经济系统、代币和交易机制详解。' }
    ]
  },
  {
    id: 'media',
    title: '媒体资源',
    icon: '🎬',
    documents: [
      { id: 'videos', title: '视频教程', content: '观看视频教程，学习游戏技巧和策略。' },
      { id: 'screenshots', title: '游戏截图', content: '浏览平面世界的精彩瞬间和美丽风景。' },
      { id: 'artwork', title: '概念艺术', content: '欣赏平面世界的概念艺术和设计灵感。' }
    ]
  }
];

const DocumentationPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(documentCategories[0].id);
  const [selectedDocument, setSelectedDocument] = useState(documentCategories[0].documents[0].id);
  const location = useLocation();

  // 页面加载动画
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // 从URL解析选中的分类和文档
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    const document = params.get('document');

    if (category && documentCategories.some(cat => cat.id === category)) {
      setSelectedCategory(category);

      const categoryObj = documentCategories.find(cat => cat.id === category);
      if (document && categoryObj.documents.some(doc => doc.id === document)) {
        setSelectedDocument(document);
      } else {
        setSelectedDocument(categoryObj.documents[0].id);
      }
    }
  }, [location]);

  // 获取当前选中的文档内容
  const getCurrentDocument = () => {
    const category = documentCategories.find(cat => cat.id === selectedCategory);
    if (!category) return null;

    return category.documents.find(doc => doc.id === selectedDocument);
  };

  // 处理分类选择
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    const category = documentCategories.find(cat => cat.id === categoryId);
    setSelectedDocument(category.documents[0].id);
  };

  // 处理文档选择
  const handleDocumentSelect = (documentId) => {
    setSelectedDocument(documentId);
  };

  // 简化装饰元素
  const decorations = [
    { id: 1, x: 10, y: 20, type: 'leaf' },
    { id: 2, x: 90, y: 30, type: 'seed' },
    { id: 3, x: 30, y: 70, type: 'leaf' },
    { id: 4, x: 80, y: 80, type: 'seed' }
  ];

  const currentDocument = getCurrentDocument();

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-blue-500/70 via-indigo-600/60 to-indigo-800/70">
      {/* 新海诚风格P5背景 */}
      <P5ShinkaiBackground />

      {/* 简化的装饰元素 */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-[3]">
        {decorations.map((item) => (
          <motion.div
            key={item.id}
            className="absolute"
            style={{
              left: `${item.x}vw`,
              top: `${item.y}vh`
            }}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 0.6, 0],
              y: [`${item.y}vh`, `${item.y - 10}vh`]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {item.type === 'leaf' && (
              <img src={leafSvg} alt="Leaf" className="w-8 h-8 opacity-50" />
            )}
            {item.type === 'seed' && (
              <img src={floatingSeeds} alt="Floating Seed" className="w-12 h-12 opacity-40" />
            )}
          </motion.div>
        ))}
      </div>

      {/* 页面返回按钮 */}
      <div className="absolute top-6 left-6 z-20">
        <Link to="/" className="text-white/80 hover:text-white transition-colors flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>返回首页</span>
        </Link>
      </div>

      {/* 主要内容 */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            className="relative z-[10] flex flex-col items-center justify-between min-h-screen px-4 py-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* 文档页面标题 */}
            <div className="w-full max-w-7xl mx-auto pt-20 pb-8">
              <div className="flex items-center justify-center mb-4">
                <motion.img
                  src={logoSvg}
                  alt="Flatland Logo"
                  className="h-16 w-auto mr-4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                />
                <motion.h1
                  className="text-4xl md:text-5xl font-bold text-white mb-0"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  平面世界文档中心
                </motion.h1>
              </div>
              <motion.p
                className="text-xl text-white/80 text-center max-w-3xl mx-auto"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                探索游戏指南、教程和资源，开启你的平面世界之旅
              </motion.p>
            </div>

            {/* 文档内容区域 */}
            <div className="w-full max-w-7xl mx-auto flex-grow flex flex-col md:flex-row gap-8 pb-20">
              {/* 左侧导航 */}
              <motion.div
                className="w-full md:w-64 flex-shrink-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 shadow-lg border border-white/20">
                  <h2 className="text-xl font-semibold text-white mb-4">文档分类</h2>
                  <nav className="space-y-1">
                    {documentCategories.map((category) => (
                      <button
                        key={category.id}
                        onClick={() => handleCategorySelect(category.id)}
                        className={`w-full text-left px-4 py-3 rounded-xl flex items-center gap-3 transition-all ${
                          selectedCategory === category.id
                            ? 'bg-white/20 text-white shadow-md'
                            : 'text-white/70 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <span className="text-xl">{category.icon}</span>
                        <span>{category.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </motion.div>

              {/* 右侧内容区 */}
              <motion.div
                className="flex-grow"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-lg border border-white/20 h-full">
                  {/* 文档子导航 */}
                  <div className="mb-6 border-b border-white/20 pb-4">
                    <div className="flex flex-wrap gap-2">
                      {documentCategories
                        .find((cat) => cat.id === selectedCategory)
                        ?.documents.map((doc) => (
                          <button
                            key={doc.id}
                            onClick={() => handleDocumentSelect(doc.id)}
                            className={`px-4 py-2 rounded-full text-sm transition-all ${
                              selectedDocument === doc.id
                                ? 'bg-blue-500/80 text-white shadow-md'
                                : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                            }`}
                          >
                            {doc.title}
                          </button>
                        ))}
                    </div>
                  </div>

                  {/* 文档内容 */}
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedDocument}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4 }}
                      className="prose prose-invert max-w-none"
                    >
                      <h2 className="text-2xl font-bold text-white mb-4">{currentDocument?.title}</h2>
                      <p className="text-white/80 leading-relaxed">{currentDocument?.content}</p>

                      {/* 示例内容 - 实际应用中可以替换为真实文档内容 */}
                      <div className="mt-8 space-y-6">
                        <p className="text-white/80">
                          平面世界是一个充满无限可能的虚拟空间，玩家可以在这里探索、创造和交互。
                          本文档将帮助你了解游戏的基本概念、操作方法和进阶技巧。
                        </p>

                        <div className="bg-white/5 p-4 rounded-xl border border-white/10">
                          <h3 className="text-lg font-medium text-white mb-2">提示</h3>
                          <p className="text-white/70 text-sm">
                            初次进入游戏时，建议先完成新手教程，熟悉基本操作和游戏机制。
                          </p>
                        </div>

                        <h3 className="text-xl font-semibold text-white mt-6">主要特点</h3>
                        <ul className="list-disc pl-5 text-white/80 space-y-2">
                          <li>去中心化的游戏世界，由区块链技术支持</li>
                          <li>玩家可以拥有和交易虚拟资产</li>
                          <li>开放式探索和任务系统</li>
                          <li>社区驱动的游戏发展和治理</li>
                        </ul>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>
            </div>

            {/* 页脚部分 */}
            <motion.footer
              className="w-full mt-auto pt-6 z-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
            >
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col md:flex-row justify-between items-center py-4 border-t border-white/20">
                  {/* Logo 和版权信息 */}
                  <div className="flex items-center mb-4 md:mb-0">
                    <img
                      src="/logo/logo-medium.png"
                      alt="Flatland Logo"
                      className="h-8 w-auto mr-3"
                    />
                    <p className="text-sm text-white opacity-80">
                      © 2025 Flat World. All rights reserved.
                    </p>
                  </div>

                  {/* 社交媒体图标 */}
                  <div className="flex space-x-6">
                    <a
                      href="https://twitter.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={twitterIcon} alt="Twitter" className="h-5 w-5" />
                    </a>
                    <a
                      href="https://github.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={githubIcon} alt="GitHub" className="h-5 w-5" />
                    </a>
                    <a
                      href="https://discord.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={discordIcon} alt="Discord" className="h-5 w-5" />
                    </a>
                    <a
                      href="/docs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="opacity-80 hover:opacity-100 transition-opacity"
                    >
                      <img src={docsIcon} alt="Documentation" className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </div>
            </motion.footer>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentationPage;
